// Discovery Bonus — v1.9.1 Multi-Way Mint §2.
//
// An "Outsider" pledge is one placed on an outcome whose implied price
// at pledge time is below the Discovery threshold. When that Outsider
// resolves as the winner, the pledger's DP is multiplied by a factor
// between DISCOVERY_MIN_MULTIPLIER and DISCOVERY_MAX_MULTIPLIER based on
// how early the signal was recorded in the market's lifetime.
//
// Earliness is a fraction in [0, 1]: 0 = pledged at the scheduled
// resolution instant (no credit), 1 = pledged at mint (full credit).
// Callers compute it as (resolveAt − pledgeAt) / (resolveAt − createdAt)
// so the helper can stay side-effect free.
//
// Pure functions — downstream ledgers call these when an Outsider wins.
// In the sandbox the multiplier is surfaced as a visible "Discovery
// Boost" chip in the pledge history; in production the settlement path
// applies the multiplier before writing the DP row.

import { calcDivergencePoints } from './divergence';

export const DISCOVERY_PRICE_THRESHOLD = 0.15;  // prices strictly below this are Outsiders
export const DISCOVERY_MIN_MULTIPLIER  = 1.5;   // floor for any Outsider win
export const DISCOVERY_MAX_MULTIPLIER  = 3.0;   // ceiling for earliest Outsider pledges

export function isOutsiderPrice(price: number): boolean {
  return price < DISCOVERY_PRICE_THRESHOLD;
}

// Multiplier for the Discovery Bonus. Returns 1.0 for non-Outsider
// pledges (so multiplying is always safe). Earliness is clamped to
// [0, 1] before scaling so a stray value from a race condition can't
// over- or under-credit the pledger.
export function calcDiscoveryMultiplier(price: number, earliness: number): number {
  if (!isOutsiderPrice(price)) return 1.0;
  const clamped = Math.max(0, Math.min(1, earliness));
  return DISCOVERY_MIN_MULTIPLIER + clamped * (DISCOVERY_MAX_MULTIPLIER - DISCOVERY_MIN_MULTIPLIER);
}

// DP with the Discovery Bonus rolled in — used only when the pledger
// was on the winning Outsider. Non-winning Outsiders and winning
// favourites both use the plain `calcDivergencePoints` helper.
export function calcDivergencePointsWithDiscovery(
  units:     number,
  price:     number,
  earliness: number,
): number {
  const base = calcDivergencePoints(units, price);
  const mult = calcDiscoveryMultiplier(price, earliness);
  return base * mult;
}

// Convenience wrapper for callers that already carry timestamps. Keeps
// the earliness math in one place so a migration to a different clock
// (sandbox-compressed vs production wall clock) only needs one edit.
export function earlinessFraction(
  pledgeAt:    number,
  createdAt:   number,
  resolveAt:   number,
): number {
  const total = resolveAt - createdAt;
  if (total <= 0) return 0;
  const remaining = resolveAt - pledgeAt;
  return Math.max(0, Math.min(1, remaining / total));
}
